import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, type Address } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { LocationFields } from '@/components/ui/LocationFields';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';

/**
 * Where the order goes.
 *
 * A first-time buyer has no saved address, so the form opens automatically
 * rather than showing an empty list with an "Add" button they have to find.
 * Sending someone to a settings screen mid-checkout — and losing their cart on
 * the way — is how a checkout loses an order it had already won.
 */
export function AddressStep({
  selectedId,
  onSelect,
  error,
}: {
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  error?: string;
}) {
  const queryClient = useQueryClient();
  const { data: addresses, isLoading } = useQuery({
    queryKey: ['addresses'],
    queryFn: ({ signal }) => api.addresses(signal),
  });

  // Tracks intent only — "the buyer asked to add one". Whether the form is
  // actually on screen is derived below.
  const [adding, setAdding] = useState(false);

  // A first-time buyer has nothing to choose from, so the form is simply open.
  // Derived during render rather than set from an effect: an effect that calls
  // setState immediately just causes a second render to show the same thing.
  const formOpen = adding || addresses?.length === 0;

  // Selecting the default IS a synchronisation — it pushes fetched data up to
  // the parent, which cannot be derived here.
  useEffect(() => {
    if (!addresses?.length || selectedId !== null) return;

    const preferred = addresses.find((a) => a.is_default) ?? addresses[0];

    if (preferred) onSelect(preferred.id);
  }, [addresses, selectedId, onSelect]);

  if (isLoading) {
    return <Skeleton className="h-24 w-full" radius="md" />;
  }

  return (
    <div className="space-y-2">
      {addresses?.map((address) => (
        <AddressOption
          key={address.id}
          address={address}
          selected={address.id === selectedId}
          onSelect={() => {
            onSelect(address.id);
            setAdding(false);
          }}
        />
      ))}

      {formOpen ? (
        <NewAddressForm
          onCancel={addresses?.length ? () => setAdding(false) : undefined}
          onCreated={(address) => {
            queryClient.setQueryData<Address[]>(['addresses'], (current) => [
              address,
              ...(current ?? []),
            ]);
            onSelect(address.id);
            setAdding(false);
          }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex min-h-11 w-full items-center justify-center gap-2 rounded-[9px] border border-dashed border-line px-3 text-[12.5px] font-bold text-blue hover:border-blue"
        >
          <Icon name="plus" size={14} strokeWidth={2.4} />
          Add a new address
        </button>
      )}

      {error ? <p className="text-[11px] font-medium text-danger">{error}</p> : null}
    </div>
  );
}

function AddressOption({
  address,
  selected,
  onSelect,
}: {
  address: Address;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 rounded-[9px] border px-3 py-3 text-left',
        selected ? 'border-[1.5px] border-blue bg-blue-tint-2' : 'border-line',
      )}
    >
      <span
        className={cn(
          'mt-0.5 grid h-[17px] w-[17px] shrink-0 place-items-center rounded border-[1.5px]',
          selected ? 'border-blue bg-blue' : 'border-line',
        )}
      >
        {selected ? <Icon name="check" size={10} strokeWidth={3.4} className="text-white" /> : null}
      </span>

      <span className="min-w-0 flex-1 text-[12px] leading-relaxed">
        <b className="block text-[12.5px]">
          {address.label}
          {address.is_default ? (
            <span className="ml-1.5 font-normal text-[10px] text-faint">default</span>
          ) : null}
        </b>
        <span className="block text-muted">{address.recipient_name}</span>
        <span className="block text-muted">{address.one_line}</span>
        <span className="block font-mono text-[11px] text-faint">{address.phone}</span>
      </span>
    </button>
  );
}

function NewAddressForm({
  onCreated,
  onCancel,
}: {
  onCreated: (address: Address) => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState({
    label: 'Home',
    recipient_name: '',
    phone: '',
    line1: '',
    lga: '',
    // Nothing preselected: a default state is a state most people never
    // notice they accepted, and an address in the wrong one is a delivery to
    // the wrong city.
    state: '',
  });

  const create = useMutation({
    mutationFn: () => api.createAddress({ ...form, line2: null }),
    onSuccess: onCreated,
  });

  const fieldErrors = create.error instanceof ApiError ? create.error.fieldErrors : {};
  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  /** The pickers hand back a value rather than an event. */
  const setValue = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <div className="space-y-3 rounded-[9px] border border-line bg-paper p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="Who is receiving it?"
          autoComplete="name"
          value={form.recipient_name}
          placeholder="Adaeze Okonkwo"
          error={fieldErrors.recipient_name?.[0]}
          onChange={set('recipient_name')}
        />
        <Input
          label="Phone for the rider"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={form.phone}
          placeholder="0803 123 4567"
          hint="The rider calls this from the street."
          error={fieldErrors.phone?.[0]}
          onChange={set('phone')}
        />
      </div>

      <Input
        label="Street address"
        autoComplete="street-address"
        value={form.line1}
        placeholder="12, Aminu Kano Crescent"
        error={fieldErrors.line1?.[0]}
        onChange={set('line1')}
      />

      {/* ── Picked, never typed ──────────────────────────────────────────
          Delivery is priced by the zone covering this address. A buyer who
          types "Wuse" matches no zone at all — and the failure is silent:
          they quietly pay the platform default instead of their real local
          fee, and nobody can see why. The district goes in the line above,
          where it belongs and where the rider actually reads it. */}
      <LocationFields
        state={form.state}
        lga={form.lga}
        onState={setValue('state')}
        onLga={setValue('lga')}
        stateError={fieldErrors.state?.[0]}
        lgaError={fieldErrors.lga?.[0]}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          loading={create.isPending}
          disabled={!form.recipient_name || !form.phone || !form.line1 || !form.state}
          onClick={() => create.mutate()}
        >
          Save address
        </Button>
        {onCancel ? (
          <Button size="sm" variant="grey" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>

      {create.error instanceof ApiError && !create.error.isValidation ? (
        <p className="text-[11px] font-medium text-danger">{create.error.message}</p>
      ) : null}
    </div>
  );
}
